/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React from 'react';
import { inputMasks, inputFormatters } from '@blend/shared-react';
import { SelectInput } from '@blend/consumer-components';
import { GenericRequiredCheckbox } from '../TaskInputs/GenericRequiredCheckbox/GenericRequiredCheckbox';
import { FORM_NAME as TASK_FORM_NAME } from '../ConnectedTaskForm';
import { MultiCheckbox } from '../Inputs/MultiCheckbox/MultiCheckbox';
import NPS from '../Inputs/NPS/NPS';
import { DeviceInputWrapper } from './DeviceInputWrapper/DeviceInputWrapper';
import { TextInputWrapper, UnformattedTextInput } from './TextInputWrapper';
import { VideoInputDevice } from './VideoInputDevice/VideoInputDevice';
import {
  dateGenerator,
  phone,
  textRequired,
  requiredCheckboxValidator,
  postalCodeValidator,
  stateCodeValidator,
  ssn,
  networkTestsValidator,
  notaryFeeValidator,
} from './validators';
import { NetworkConnection } from './NetworkConnection/NetworkConnection';
import { AudioOutputDevice } from './AudioOutputDevice/AudioOutputDevice';
import { AudioInputDevice } from './AudioInputDevice/AudioInputDevice';
import {
  InputConfigWithChoosers,
  ComplexInputMap,
  ComplexInputProps,
  FieldPropsMap,
  InputConfigWithChoices,
  RequiredCheckboxInputConfig,
  MultiCheckboxInputConfig,
  NetworkConnectionInputConfig,
} from './types';
import { MoneyInputWrapper } from './MoneyInputWrapper';
import { MoneyInputConfig } from './types';
import { Chooser } from '@components/form/Inputs/Chooser';
import AddressInput from '@components/form/Inputs/AddressInput/AddressInput';
import StateInput from '@components/form/Inputs/AddressInput/StateInput/StateInput';
import { LongText } from '@components/form/Inputs/LongText/LongText';
import { RadioGroup } from '@components/form/Inputs/RadioGroup/RadioGroup';

export const fieldPropsMap: FieldPropsMap = {
  text: {
    name: 'text',
    inputComponent: UnformattedTextInput,
    requiredValidator: textRequired,
  },
  date: {
    name: 'date',
    validate: dateGenerator,
    normalize: inputFormatters.DATE.normalize,
    inputComponent: TextInputWrapper({ mask: inputMasks.DATE }),
  },
  longText: {
    name: 'longText',
    inputComponent({ config, input, meta, ...htmlProps }) {
      return <LongText {...input} {...htmlProps} />;
    },
    requiredValidator: textRequired,
  },
  phone: {
    name: 'phone',
    validate: phone,
    normalize: inputMasks.PHONE.normalize,
    inputComponent: TextInputWrapper({ mask: inputMasks.PHONE, forceNumericKeyboard: true }),
  },
  ssn: {
    name: 'ssn',
    validate: ssn,
    normalize: inputMasks.SSN.normalize,
    inputComponent: TextInputWrapper({ mask: inputMasks.SSN }),
  },
  radio: {
    name: 'radio',
    inputComponent({ config, input, meta, ...htmlProps }) {
      return (
        <RadioGroup
          {...htmlProps}
          legend={config.label!}
          choices={(config as InputConfigWithChoices).choices}
          name={config.id}
          value={input.value}
          onChange={input.onChange}
        />
      );
    },
  },
  chooser: {
    name: 'chooser',
    inputComponent({ config, input, meta, ...htmlProps }) {
      return (
        <Chooser
          {...htmlProps}
          choosers={(config as InputConfigWithChoosers).choosers}
          formName={TASK_FORM_NAME}
          onChoiceSelect={input.onChange}
        />
      );
    },
  },
  multi_checkbox: {
    name: 'multi_checkbox',
    inputComponent({ config, input, meta, ...htmlProps }) {
      return (
        <MultiCheckbox
          {...htmlProps}
          legend={(config as MultiCheckboxInputConfig).display}
          choices={(config as MultiCheckboxInputConfig).choices}
          name={(config as MultiCheckboxInputConfig).text}
          value={input.value}
          onChange={input.onChange}
        />
      );
    },
  },
  dropdown: {
    name: 'dropdown',
    inputComponent({ config, input, meta, ...htmlProps }) {
      return (
        <SelectInput
          {...htmlProps}
          choices={(config as InputConfigWithChoices).choices}
          value={input.value}
          onChange={input.onChange}
        />
      );
    },
  },
  boolean_required_true: {
    name: 'boolean_required_true',
    requiredValidator: requiredCheckboxValidator,
    normalize: Boolean,
    inputComponent({ config, input, meta, ...htmlProps }) {
      return (
        <GenericRequiredCheckbox
          {...htmlProps}
          name={config.id}
          checked={!!input.value}
          onChange={input.onChange}
          displayParts={(config as RequiredCheckboxInputConfig).displayParts}
          id={htmlProps.id || config.id}
        />
      );
    },
  },
  boolean: {
    name: 'boolean',
    normalize: Boolean,
    inputComponent({ config, input, meta, ...htmlProps }) {
      return (
        <GenericRequiredCheckbox
          {...htmlProps}
          name={config.id}
          checked={!!input.value}
          onChange={input.onChange}
          displayParts={(config as RequiredCheckboxInputConfig).displayParts}
          id={htmlProps.id || config.id}
        />
      );
    },
  },
  video_input: {
    name: 'video_input',
    inputComponent({ config, input, meta, ...htmlProps }) {
      return (
        <DeviceInputWrapper label={'Video'}>
          <VideoInputDevice {...htmlProps} name={config.id} onChange={input.onChange} />
        </DeviceInputWrapper>
      );
    },
  },
  audio_output: {
    name: 'audio_output',
    inputComponent({ config, input, meta, ...htmlProps }) {
      return (
        <DeviceInputWrapper label={'Speakers'}>
          <AudioOutputDevice {...htmlProps} name={config.id} onChange={input.onChange} />
        </DeviceInputWrapper>
      );
    },
  },
  audio_input: {
    name: 'audio_input',
    inputComponent({ config, input, meta, ...htmlProps }) {
      return (
        <DeviceInputWrapper label={'Microphone'}>
          <AudioInputDevice {...htmlProps} name={config.id} onChange={input.onChange} />
        </DeviceInputWrapper>
      );
    },
  },
  network_connection: {
    name: 'network_connection',
    requiredValidator: networkTestsValidator,
    inputComponent({ config, input }) {
      const { showDeviceCompatibilityStatus } = config as NetworkConnectionInputConfig;

      return (
        <DeviceInputWrapper label={'Internet Connection'}>
          <NetworkConnection
            onNetworkTestsCompletion={input.onChange}
            showDeviceCompatibilityStatus={showDeviceCompatibilityStatus}
          />
        </DeviceInputWrapper>
      );
    },
  },
  postal_code: {
    name: 'postalCode',
    validate: postalCodeValidator,
    inputComponent: UnformattedTextInput,
  },
  state_code: {
    name: 'stateCode',
    validate: stateCodeValidator,
    inputComponent: StateInput,
  },
  nps: {
    name: 'nps',
    inputComponent({ config, input }) {
      return <NPS ratingValue={input.value} onChange={input.onChange} label={config.label} />;
    },
  },
  notary_fee: {
    name: 'fees',
    validate: notaryFeeValidator,
    inputComponent({ config, input, meta, ...htmlProps }) {
      return (
        <MoneyInputWrapper
          htmlProps={{ ...htmlProps } as React.InputHTMLAttributes<HTMLInputElement>}
          defaultValue={(config as MoneyInputConfig).value}
          onChange={input.onChange}
        />
      );
    },
  },
};

// for any input that does not map to a single Field
export const complexInputMap: ComplexInputMap = {
  address: (props: ComplexInputProps) => {
    const { config } = props;
    return (
      <AddressInput
        id={config.id}
        required={config.required}
        googleApiKey={config.googleApiKey}
        display={config.label ?? config.display}
      />
    );
  },
};
